import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputAdornment,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RemoveIcon from '@mui/icons-material/Remove';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Dialog, Button } from '../../../../../../Components/MUIs';
import { useMemo } from 'react';
import {
  GetProcessDisplayName,
  GetProcessesForDepartment,
  NormalizeDepCdKey,
  ProcessesMatchForSelection,
} from '../SafetyInfo_Schema';

const ProcessRowStableId = (proc, listIndex) => {
  const dep = NormalizeDepCdKey(proc?.depCd);
  const cd = String(proc?.gongJongCd ?? '').trim();
  const nm = String(proc?.gongJongNm ?? '').trim();
  const base = `${dep}_${cd}_${nm}`.replace(/\s/g, '_');
  return `${base}_i${listIndex}`;
};

/** 드래그용: 위치(index)와 무관하게 동일 공정은 동일 id (순서 변경 시 유지) */
const SelectedItemDragId = (proc) => {
  const dep = NormalizeDepCdKey(proc?.depCd);
  const cd = String(proc?.gongJongCd ?? '').trim();
  const nm = String(proc?.gongJongNm ?? '').trim();
  return `s-${dep}-${cd}-${nm}`.replace(/[^a-zA-Z0-9_-]/g, '_');
};

/**
 * ProcessSelectModal Component
 * Three-panel modal for selecting processes from departments
 *
 * Structure:
 * - Left panel: Department selection with search and checkboxes
 * - Middle panel: Process list filtered by selected departments with search
 * - Right panel: Selected processes with drag-drop reordering
 *
 * @param {Object} props
 * @param {boolean} props.open - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onConfirm - Confirm handler (called with selected processes)
 * @param {Array} props.depData - Department list data
 * @param {Object} props.processDataByDept - Processes grouped by department code
 * @param {Array} props.selectedDepartments - Currently selected departments
 * @param {Function} props.onToggleDepartment - Handler for department selection toggle
 * @param {Array} props.filteredProcessList - Filtered process list based on selected departments
 * @param {Array} props.selectedProcessCodes - Currently selected process codes
 * @param {Function} props.onToggleProcessCode - Handler for process selection toggle
 * @param {Function} props.onDragEnd - Drag end handler for reordering
 * @param {Function} props.onClearAll - Clear all selections handler
 * @param {string} props.departmentSearchText - Department search text
 * @param {Function} props.setDepartmentSearchText - Department search text setter
 * @param {string} props.processSearchText - Process search text
 * @param {Function} props.setProcessSearchText - Process search text setter
 * @returns {JSX.Element} ProcessSelectModal component
 */
const ProcessSelectModal = ({
  open,
  onClose,
  onConfirm,
  depData = [],
  processDataByDept = {},
  selectedDepartments = [],
  onToggleDepartment,
  filteredProcessList = [],
  selectedProcessCodes = [],
  onToggleProcessCode,
  onDragEnd,
  onClearAll,
  departmentSearchText = '',
  setDepartmentSearchText,
  processSearchText = '',
  setProcessSearchText,
  onOpenRegisterModal,
}) => {
  /**
   * Filter departments by search text
   */
  const safeDepData = useMemo(
    () => (Array.isArray(depData) ? depData : []),
    [depData]
  );
  const filteredDepartments = safeDepData.filter(
    (dep) =>
      !departmentSearchText ||
      dep.label?.toLowerCase().includes(departmentSearchText.toLowerCase())
  );

  /** depNm 누락 시 왼쪽 부서 목록(depData)으로 보강 */
  const depLabelByCd = useMemo(() => {
    const m = new Map();
    for (const d of safeDepData) {
      m.set(NormalizeDepCdKey(d.value), String(d.label ?? '').trim());
    }
    return m;
  }, [safeDepData]);

  const GetProcessDepartmentLabel = (proc) => {
    const nm = String(proc?.depNm ?? '').trim();
    if (nm) return nm;
    const k = NormalizeDepCdKey(proc?.depCd);
    if (!k) return '';
    return depLabelByCd.get(k) ?? '';
  };

  const FormatDepartmentLine = (proc) => {
    const s = GetProcessDepartmentLabel(proc);
    return s ? `부서: ${s}` : '';
  };
  /**
   * 체크된 부서들의 공정을 합침.
   * gongJongCd가 비어 있는 행이 많으면 depCd+gongJongCd만으로는 전부 같은 키가 되어 1건만 남으므로
   * gongJongNm(및 필요 시 순번)까지 포함해 구분한다.
   */
  const currentProcessList = useMemo(() => {
    if (!selectedDepartments?.length) return [];
    const seen = new Set();
    const out = [];
    for (const dept of selectedDepartments) {
      const list = GetProcessesForDepartment(processDataByDept, dept.value);
      for (const proc of list) {
        const depK = NormalizeDepCdKey(proc.depCd);
        const gj = String(proc.gongJongCd ?? '').trim();
        const nm = String(proc.gongJongNm ?? '').trim();
        const base = `${depK}|${gj}|${nm}`;
        let candidate = base;
        let suffix = 0;
        while (seen.has(candidate)) {
          suffix += 1;
          candidate = `${base}::${suffix}`;
        }
        seen.add(candidate);
        out.push(proc);
      }
    }
    return out;
  }, [selectedDepartments, processDataByDept]);
  /**
   * Filter processes by search text
   */
  const filteredProcesses = currentProcessList.filter(
    (proc) =>
      !processSearchText ||
      proc.gongJongNm?.toLowerCase().includes(processSearchText.toLowerCase())
  );

  return (
    <Dialog
      title="공정 선택"
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px',
        },
      }}
      actions={
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            name="취소"
            onClick={onClose}
            sx={{
              color: '#fff',
              backgroundColor: '#D32F2F',
              '&:hover': {
                backgroundColor: '#B71C1C',
              },
            }}
            size="small"
          />
          <Button
            name="선택하기"
            onClick={onConfirm}
            sx={{
              color: '#fff',
              backgroundColor: '#2CA900',
              '&:hover': {
                backgroundColor: '#249100',
              },
            }}
            size="small"
          />
        </Box>
      }
      useSaveButton={false}
      useCloseButton={false}
      content={
        <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
          {/* Left Panel: Department Selection */}
          <Paper
            sx={{
              flex: 1,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#f8f9fa',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 1.5, color: '#333' }}
            >
              부서를 검색하세요
            </Typography>
            <TextField
              size="small"
              placeholder="부서를 검색하세요"
              value={departmentSearchText}
              onChange={(e) => setDepartmentSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <List
              sx={{
                flex: 1,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fff',
              }}
            >
              {filteredDepartments.map((dep) => {
                const checked = selectedDepartments.some(
                  (d) =>
                    NormalizeDepCdKey(d.value) === NormalizeDepCdKey(dep.value)
                );
                const procCount = GetProcessesForDepartment(
                  processDataByDept,
                  dep.value
                ).length;
                return (
                  <ListItem key={dep.value} disablePadding>
                    <ListItemButton
                      onClick={() => onToggleDepartment(dep)}
                      sx={{
                        backgroundColor: checked ? '#e8f5e9' : 'transparent',
                        border: checked ? '1px solid #4caf50' : 'none',
                        borderRadius: 3,
                        mx: 0.5,
                        my: 0.25,
                        '&:hover': {
                          backgroundColor: checked ? '#c8e6c9' : '#f5f5f5',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={checked}
                          disableRipple
                          tabIndex={-1}
                          sx={{ pointerEvents: 'none' }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${dep.label} (${procCount})`}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Paper>

          {/* Middle Panel: Process List */}
          <Paper
            sx={{
              flex: 1,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#f8f9fa',
            }}
          >
            <TextField
              size="small"
              placeholder="검색어를 입력하세요"
              value={processSearchText}
              onChange={(e) => setProcessSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ color: '#666' }}>
                {selectedDepartments.length > 0
                  ? `선택한 부서 공정 (${filteredProcesses.length})`
                  : `공정 (${filteredProcesses.length})`}
              </Typography>
              {selectedDepartments.length === 1 ? (
                <Button
                  size="small"
                  name="공정 등록"
                  onClick={onOpenRegisterModal}
                  sx={{
                    color: '#fff',
                    backgroundColor: '#2CA900',
                    '&:hover': {
                      backgroundColor: '#249100',
                    },
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                  }}
                />
              ) : (
                <Tooltip
                  title="공정 등록은 왼쪽에서 부서를 정확히 하나만 선택한 뒤 사용할 수 있습니다."
                  placement="top"
                >
                  <span>
                    <Button
                      size="small"
                      name="공정 등록"
                      onClick={onOpenRegisterModal}
                      disabled
                      sx={{
                        color: '#fff',
                        backgroundColor: '#9e9e9e',
                        fontSize: '0.75rem',
                        px: 1.5,
                        py: 0.5,
                        '&.Mui-disabled': {
                          color: '#fff',
                          backgroundColor: '#9e9e9e',
                        },
                      }}
                    />
                  </span>
                </Tooltip>
              )}
            </Box>
            <List
              sx={{
                flex: 1,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fff',
              }}
            >
              {selectedDepartments.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="왼쪽에서 부서를 체크하면 해당 부서들의 공정이 표시됩니다"
                    sx={{ color: '#999', textAlign: 'center' }}
                  />
                </ListItem>
              ) : (
                filteredProcesses.map((proc, idx) => (
                  <ListItem
                    key={ProcessRowStableId(proc, idx)}
                    disablePadding
                  >
                    <ListItemButton
                      onClick={() => onToggleProcessCode(proc)}
                      sx={{
                        backgroundColor: selectedProcessCodes.some((p) =>
                          ProcessesMatchForSelection(p, proc)
                        )
                          ? '#e8f5e9'
                          : 'transparent',
                        border: selectedProcessCodes.some((p) =>
                          ProcessesMatchForSelection(p, proc)
                        )
                          ? '1px solid #4caf50'
                          : 'none',
                        borderRadius: 3,
                        mx: 0.5,
                        my: 0.25,
                        '&:hover': {
                          backgroundColor: selectedProcessCodes.some((p) =>
                            ProcessesMatchForSelection(p, proc)
                          )
                            ? '#c8e6c9'
                            : '#f5f5f5',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedProcessCodes.some((p) =>
                            ProcessesMatchForSelection(p, proc)
                          )}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={GetProcessDisplayName(proc)}
                        secondary={FormatDepartmentLine(proc)}
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>

          {/* Right Panel: Selected Processes with Drag-Drop */}
          <Paper
            sx={{
              flex: 1,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#f8f9fa',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: '#2CA900' }}
              >
                {selectedProcessCodes.length}개 선택됨
              </Typography>
              <Button
                size="small"
                name="전체 삭제"
                onClick={onClearAll}
                sx={{
                  color: '#f44336',
                  fontSize: '0.75rem',
                  backgroundColor: '#fff',
                  border: '1px solid #f44336',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: '#ffebee',
                    borderColor: '#d32f2f',
                  },
                }}
                variant="outlined"
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fff',
                p: 1,
              }}
            >
              {selectedProcessCodes.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#999',
                  }}
                >
                  <Typography variant="body2">선택한 공정이 없어요</Typography>
                  <Typography variant="caption">
                    선택한 항목을 여기에서 확인하실 수 있어요
                  </Typography>
                </Box>
              ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="selected-processes">
                    {(provided) => (
                      <Stack
                        spacing={1}
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {selectedProcessCodes.map((proc, index) => {
                          const depLine = FormatDepartmentLine(proc);
                          return (
                          <Draggable
                            key={SelectedItemDragId(proc)}
                            draggableId={SelectedItemDragId(proc)}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1.5,
                                  backgroundColor: snapshot.isDragging
                                    ? '#e3f2fd'
                                    : '#f5f5f5',
                                  borderRadius: 1,
                                  boxShadow: snapshot.isDragging ? 2 : 0,
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flex: 1,
                                  }}
                                >
                                  <Box
                                    {...provided.dragHandleProps}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      cursor: 'grab',
                                      mr: 1,
                                      color: '#666',
                                      '&:active': {
                                        cursor: 'grabbing',
                                      },
                                    }}
                                  >
                                    <DragIndicatorIcon fontSize="small" />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {GetProcessDisplayName(proc)}
                                    </Typography>
                                    {depLine ? (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: '#666', display: 'block' }}
                                      >
                                        {depLine}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => onToggleProcessCode(proc)}
                                  sx={{ color: '#f44336' }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </Stack>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Box>
          </Paper>
        </Box>
      }
    />
  );
};

export default ProcessSelectModal;
